import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ICONS, SPECIALIZATIONS, SOFTWARE } from '../constants';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import SimpleCaptcha from '../components/SimpleCaptcha';
import { API_URL } from '../config';
import { useTranslation } from '../hooks/useTranslation';
import LanguageToggle from '../components/LanguageToggle';

const PublicApply: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, setLanguage } = useTranslation();

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenMessage, setTokenMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    specialization: '',
    software: [] as string[],
    telegram: '',
    contact: '',
    portfolioUrl: '',
    description: '',
    location: '',
  });

  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/invite/${token}`);
      const data = await response.json();

      if (data.valid) {
        setTokenValid(true);
        // Set language from invite if available
        if (data.language) {
          setLanguage(data.language);
        }
      } else {
        setTokenValid(false);
        setTokenMessage(data.message || 'Invalid or expired invite link');
      }
    } catch (err) {
      setTokenValid(false);
      setTokenMessage('Failed to validate invite link');
    } finally {
      setValidating(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSoftwareToggle = (software: string) => {
    setFormData(prev => ({
      ...prev,
      software: prev.software.includes(software)
        ? prev.software.filter(s => s !== software)
        : [...prev.software, software],
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValid) {
      setError(t('invite.captchaRequired'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('specialization', formData.specialization);
      submitData.append('software', JSON.stringify(formData.software));
      if (formData.telegram) submitData.append('telegram', formData.telegram);
      if (formData.contact) submitData.append('contact', formData.contact);
      if (formData.portfolioUrl) submitData.append('portfolioUrl', formData.portfolioUrl);
      if (formData.description) submitData.append('description', formData.description);
      if (formData.location) submitData.append('location', formData.location);
      if (avatar) submitData.append('avatar', avatar);

      const response = await fetch(`${API_URL}/api/v1/invite/${token}/submit`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit application');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <ICONS.AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{t('invite.invalidToken')}</h1>
          <p className="text-gray-400 mb-6">{tokenMessage}</p>
          <p className="text-sm text-gray-500">
            {t('invite.tokenMessage')}
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <ICONS.Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('invite.success')}</h1>
          <p className="text-gray-400 mb-6">
            {t('invite.successMessage')}
          </p>
          <p className="text-sm text-gray-500">
            {t('invite.closeWindow')}
          </p>
        </div>
      </div>
    );
  }

  // Application form
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4 overflow-x-hidden">
      <div className="max-w-3xl mx-auto w-full min-w-0">
        {/* Header */}
        <div className="text-center mb-8 relative">
          {/* Language toggle in top right */}
          <div className="absolute top-0 right-0">
            <LanguageToggle />
          </div>
          
          <div className="flex items-center justify-center space-x-2 mb-4">
            <ICONS.BookOpen className="w-10 h-10 text-indigo-400" />
            <h1 className="text-3xl font-bold text-white">VFX SpellBook</h1>
          </div>
          <p className="text-xl text-gray-300">{t('invite.applicationTitle')}</p>
          <p className="text-sm text-gray-500 mt-2">
            {t('invite.applicationSubtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg shadow-xl p-6 space-y-6 min-w-0 overflow-x-hidden">
          {error && (
            <div className="bg-red-900/30 border border-red-600/50 rounded p-4">
              <div className="flex items-center space-x-2">
                <ICONS.AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-200">{error}</p>
              </div>
            </div>
          )}

          {/* Avatar */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('invite.profilePicture')}
            </label>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  formData.name.split(' ').map(n => n[0]).join('').toUpperCase() || '?'
                )}
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleAvatarChange}
                className="text-sm text-gray-400"
              />
            </div>
          </div>

          {/* Name */}
          <Input
            label={`${t('invite.fullName')} *`}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />

          {/* Email */}
          <Input
            label={`${t('invite.email')} *`}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />

          {/* Specialization */}
          <Select
            label={`${t('invite.specialization')} *`}
            value={formData.specialization}
            onChange={(e) => handleInputChange('specialization', e.target.value)}
            required
          >
            <option value="">{t('invite.selectSpecialization')}</option>
            {SPECIALIZATIONS.map(spec => (
              <option key={spec} value={spec}>{spec}</option>
            ))}
          </Select>

          {/* Software */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('invite.software')} *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SOFTWARE.map(soft => (
                <label
                  key={soft}
                  className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                    formData.software.includes(soft)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.software.includes(soft)}
                    onChange={() => handleSoftwareToggle(soft)}
                    className="hidden"
                  />
                  <span className="text-sm">{soft}</span>
                </label>
              ))}
            </div>
            {formData.software.length === 0 && (
              <p className="text-xs text-red-400 mt-1">{t('invite.selectAtLeastOne')}</p>
            )}
          </div>

          {/* Telegram */}
          <Input
            label={t('invite.telegram')}
            value={formData.telegram}
            onChange={(e) => handleInputChange('telegram', e.target.value)}
            placeholder="@username"
          />

          {/* Phone */}
          <Input
            label={t('invite.phone')}
            value={formData.contact}
            onChange={(e) => handleInputChange('contact', e.target.value)}
            placeholder="+1234567890"
          />

          {/* Location */}
          <Input
            label={t('invite.location')}
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="City, Country"
          />

          {/* Portfolio URL */}
          <Input
            label={t('invite.portfolioUrl')}
            type="url"
            value={formData.portfolioUrl}
            onChange={(e) => handleInputChange('portfolioUrl', e.target.value)}
            placeholder="https://"
          />

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('invite.aboutYou')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className="w-full min-w-0 max-w-full box-border px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('invite.aboutPlaceholder')}
            />
          </div>

          {/* Captcha */}
          <SimpleCaptcha onValidate={setCaptchaValid} />

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || !captchaValid || formData.software.length === 0}
            className="w-full justify-center !py-3 text-lg"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('invite.submitting')}
              </>
            ) : (
              t('invite.submitApplication')
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PublicApply;

